import {skipToken} from '@reduxjs/toolkit/query';
import {Alert, Button} from 'platform/components';
import {Heading, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil} from 'ramda';

import {
  useGetWhatsAppVerificationQuery,
  useSendWhatsAppVerificationMessageMutation,
  useStartWhatsAppVerificationMutation,
  WhatsAppVerificationResponseBody,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

// Meta's webhooks for the inbound code and the delivery report usually arrive within seconds.
const POLLING_INTERVAL_MS = 3000;

const isWaitingForMeta = (verification: WhatsAppVerificationResponseBody | undefined) =>
  verification?.state === 'awaiting_inbound' || verification?.state === 'awaiting_delivery';

const formatPhoneNumber = (phoneNumber: string | null) =>
  isNil(phoneNumber) ? '' : `+${phoneNumber}`;

/**
 * Guided check of both directions: the user texts a code to the business number (receiving),
 * then confirms the number it came from before Omnetic sends a reply to it (sending).
 */
export function WhatsAppVerification() {
  const [started, setStarted] = useState<WhatsAppVerificationResponseBody | null>(null);
  const [pollingInterval, setPollingInterval] = useState(0);

  const [startVerification, {isLoading: isStarting}] = useStartWhatsAppVerificationMutation();
  const [sendMessage, {isLoading: isSending}] = useSendWhatsAppVerificationMessageMutation();
  // currentData, not data: after "Start again" the previous verification must not linger.
  const {currentData: polled} = useGetWhatsAppVerificationQuery(
    isNil(started) ? skipToken : {verificationId: started.id},
    {pollingInterval}
  );
  // The start response stands in until the first poll returns.
  const verification = polled ?? started ?? undefined;

  // Poll only while Omnetic waits for Meta; RTK Query re-reads the interval on every render.
  const nextPollingInterval = isWaitingForMeta(verification) ? POLLING_INTERVAL_MS : 0;
  if (pollingInterval !== nextPollingInterval) {
    setPollingInterval(nextPollingInterval);
  }

  const start = () => startVerification().unwrap().then(setStarted).catch(handleApiError);

  const sendReply = (confirmed: WhatsAppVerificationResponseBody) => {
    if (isNil(confirmed.senderPhoneNumber)) {
      return;
    }
    sendMessage({verificationId: confirmed.id, recipientPhoneNumber: confirmed.senderPhoneNumber})
      .unwrap()
      .catch(handleApiError);
  };

  const sendReplyAgainButton = (current: WhatsAppVerificationResponseBody) => (
    <Button
      variant="primary"
      title={i18n.t('page.settings.whatsApp.verification.sendReplyAgain', {
        sender: formatPhoneNumber(current.senderPhoneNumber),
      })}
      isLoading={isSending}
      onClick={() => sendReply(current)}
    />
  );

  const startButton = (title: string) => (
    <Button variant="secondary" title={title} isLoading={isStarting} onClick={start} />
  );

  if (isNil(verification)) {
    return (
      <VStack spacing={2}>
        <Text size="small">{i18n.t('page.settings.whatsApp.verification.description')}</Text>
        {startButton(i18n.t('page.settings.whatsApp.verification.start'))}
      </VStack>
    );
  }

  const sender = formatPhoneNumber(verification.senderPhoneNumber);

  return match(verification.state)
    .with('awaiting_inbound', () => (
      <VStack spacing={2}>
        <Text size="small">{i18n.t('page.settings.whatsApp.verification.sendCode')}</Text>
        <Heading size={3}>{verification.code}</Heading>
        <Text size="small" color="secondary">
          {i18n.t('page.settings.whatsApp.verification.waitingForCode')}
        </Text>
      </VStack>
    ))
    .with('awaiting_confirmation', () => (
      <VStack spacing={2}>
        <Alert
          variant="success"
          title={i18n.t('page.settings.whatsApp.verification.receivingVerified')}
          message={i18n.t('page.settings.whatsApp.verification.codeReceivedFrom', {sender})}
        />
        <Text size="small">{i18n.t('page.settings.whatsApp.verification.confirmRecipient')}</Text>
        <Button
          variant="primary"
          title={i18n.t('page.settings.whatsApp.verification.sendReplyTo', {sender})}
          isLoading={isSending}
          onClick={() => sendReply(verification)}
        />
      </VStack>
    ))
    .with('awaiting_delivery', () => (
      <VStack spacing={2}>
        <Text size="small" color="secondary">
          {i18n.t('page.settings.whatsApp.verification.waitingForDelivery', {sender})}
        </Text>
        {/* Meta never confirmed delivery within the backend's window, so the reply may be resent. */}
        <Show when={verification.canSendMessage}>{sendReplyAgainButton(verification)}</Show>
      </VStack>
    ))
    .with('sending_failed', () => (
      <VStack spacing={2}>
        <Alert
          variant="error"
          title={i18n.t('page.settings.whatsApp.verification.sendingFailed')}
          // No code when Meta accepted the reply but reported it undelivered.
          message={
            isNil(verification.sendingFailureMetaErrorCode)
              ? undefined
              : i18n.t('page.settings.whatsApp.connection.metaErrorCode', {
                  code: verification.sendingFailureMetaErrorCode,
                })
          }
        />
        {sendReplyAgainButton(verification)}
      </VStack>
    ))
    .with('completed', () => (
      <Alert variant="success" title={i18n.t('page.settings.whatsApp.verification.completed')} />
    ))
    .with('expired', 'superseded', (state) => (
      <VStack spacing={2}>
        <Alert
          variant="warning"
          title={
            state === 'expired'
              ? i18n.t('page.settings.whatsApp.verification.expired')
              : i18n.t('page.settings.whatsApp.verification.superseded')
          }
        />
        {startButton(i18n.t('page.settings.whatsApp.verification.startAgain'))}
      </VStack>
    ))
    .exhaustive();
}
